import {AbstractInputComponent} from "../abstract-input.component";
import {EmployeeToIssueAssignment} from "../../../domain/entities/employee-to-issue-assignment.entity";
import {property} from "lit/decorators/property.js";
import {customElement} from "lit/decorators/custom-element.js";
import {html} from "lit";
import {translate} from "lit-translate";
import {Employee} from "../../../domain/entities/employee.entity";
import {state} from "lit/decorators/state.js";
import {repeat} from "lit/directives/repeat.js";
import {container} from "tsyringe";
import {
    EmployeeToIssueAssignmentRepository
} from "../../../domain/repositories/employee-to-issue-assignment.repository";
import {Issue} from "../../../domain/entities/issue.entity";
import {DateHelper} from "../../../helpers/date.helper";
import {EmployeeRepository} from "../../../domain/repositories/employee.repository";

type ListItem = {
    uid: string
    employee: Employee | null
    issue: Issue | null,
    timeBudget: number,
    isDeleted: boolean
}

@customElement(`ha-employee-issue-assignment`)
export class HaEmployeeIssueAssignment extends AbstractInputComponent {

    @property({type: Object})
    public issue: Issue | null = null;

    @property({type: Number})
    public max: number = Infinity;

    private _employeeIssueAssignmentRepository = container.resolve(EmployeeToIssueAssignmentRepository);

    @state()
    private _assignments: ListItem[] = [];

    private _value!: EmployeeToIssueAssignment[];

    @property({type: Array})
    public get value(): EmployeeToIssueAssignment[] {
        return this._value;
    }

    public set value(value: EmployeeToIssueAssignment[]) {
        if (this._value === value) {
            return;
        }

        this._value = value;

        Promise.all(this.value.map(async employeeToIssueAssignment => {
            let employee = await employeeToIssueAssignment.employee;

            // If employee not found (possibly deleted), try with includeDeleted
            if (!employee && employeeToIssueAssignment.employee?.uid) {
                const employeeRepository = container.resolve(EmployeeRepository);
                employee = await employeeRepository.findOneById(employeeToIssueAssignment.employee.uid, true);
            }

            return <ListItem>{
                uid: employeeToIssueAssignment.uid,
                employee: employee,
                issue: await employeeToIssueAssignment.issue,
                timeBudget: employeeToIssueAssignment.timeBudget,
                isDeleted: !!(employee as any)?.isDeleted || !!(employee as any)?.deletedAt
            };
        })).then(assignments => this._assignments = assignments);

        if ((this.value ?? []).length === 0) {
            this._onClickAdd().then();
        }
    }

    private get _totalTimeBudget(): string {
        const minutes = this._assignments.reduce((sum, assignment) => {
            sum += assignment.timeBudget;

            return sum;
        }, 0);

        return DateHelper.convertDurationToString(minutes);
    }

    private get _isReadOnly(): boolean {
        return this.readonly;
    }

    protected render(): unknown {

        return html`
            <label>
                <div class="employee">${translate(`components.ha-employee-issue-assignment.head.employee`)}</div>
            </label>
            <div class="body">
                ${repeat(this._assignments, a => a.uid, (listItem: ListItem, index: number) => {

                    return html`
                        <div class="combobox-row-assignments ${listItem.isDeleted ? 'is-deleted' : ''}">
                            <ha-combobox data-type="assignee" class="max9999-flexrow"
                                         .value=${listItem.employee}
                                         .entity=${Employee}
                                         with-filter
                                         ?readonly=${this._isReadOnly || listItem.isDeleted}
                                         title=${listItem.isDeleted ? 'Dieser Mitarbeiter wurde gelöscht' : ''}
                                         @value-changed=${(evt: CustomEvent<{ value: Employee }>) => this._onEmployeeChanged(listItem, evt.detail.value, index)}
                            ></ha-combobox>
                            <button type="button"
                                    class="delete buttonsize-icon buttonsize-normal"
                                    @click=${() => this._onClickDelete(listItem)}
                            >
                                <div class="hover-effect"></div>
                            </button>
                            <ha-input-duration data-type="timeBudget" class=""
                                               postfix="h"
                                               .value=${listItem.timeBudget}
                                               ?readonly=${this._isReadOnly || listItem.isDeleted}
                                               @value-changed=${(evt: CustomEvent<{ value: number }>) => this._onTimeBudgetChanged(listItem, evt.detail.value, index)}
                            ></ha-input-duration>

                        </div>
                    `
                })}
                <div class="footer">
                    <button type="button"
                            ?disabled=${this.value.length >= this.max}
                            class="add-employee buttonsize-icon buttonsize-normal"
                            @click=${() => this._onClickAdd()}
                    >
                        <div class="hover-effect"></div>
                    </button>
                    <div class="sum-facts">
                        <span class="sum-title">${translate(`components.ha-employee-issue-assignment.foot.total`)}</span>
                        <span class="sum-value">${this._totalTimeBudget}</span>
                    </div>
                </div>
            </div>            
            ${this._renderHelperText()}
        `;
    }

    private _onClickDelete(listItem: ListItem): void {
        this._assignments = this._assignments.filter(a => a !== listItem);
        this._value = this._value.filter(assignment => !(
            assignment.employee?.uid === listItem.employee?.uid
            && assignment.issue?.uid === listItem.issue?.uid
        ));
        this.fire(this._value);
    }

    private async _onEmployeeChanged(listItem: ListItem, value: Employee, index: number): Promise<void> {
        if (listItem.employee === value) {
            return;
        }
        listItem.employee = value;

        const newAssignment = await container.resolve(EmployeeToIssueAssignmentRepository).create(listItem.issue!, value);
        if (!newAssignment) {
            throw new Error(`No new employee to issue assignment created`);
        }
        newAssignment.timeBudget = listItem.timeBudget;


        this._value = [...this._value.slice(0, index), newAssignment, ...this._value.slice(index + 1)];
        this.fire(this._value);
    }

    private _onTimeBudgetChanged(listItem: ListItem, value: number, index: number): void {
        if (listItem.timeBudget === value) {
            return;
        }

        // Mutate the EXISTING tracked assignment in place: keeping its UID lets the
        // ChangeTracker mark it DIRTY, so the builder emits ONE update command instead
        // of delete + create with a fresh UID (O2D-PRD-104). `this._value[index]` is the
        // tracked proxy (index-aligned with `_assignments`); `listItem.timeBudget` is the
        // UI-only sync for the list row.
        this._value[index].timeBudget = listItem.timeBudget = value;

        this.fire(this._value);
    }

    private async _onClickAdd(): Promise<void> {
        const issue = this.issue;
        if (!issue || this.value.length >= this.max) {
            return;
        }

        const assignment = await this._employeeIssueAssignmentRepository.create(issue, null);
        if (!assignment) {
            return;
        }

        this.value = [...this._value, assignment];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ha-employee-issue-assignment': HaEmployeeIssueAssignment;
    }
}